/**
 * Cookie-based JWT auth helpers.
 * Used when AUTH_MODE = "cookie-only" or "dual".
 * Note: Cookies set client-side cannot be HttpOnly.
 * For HttpOnly, the backend must set the cookie via Set-Cookie header.
 * This module provides a migration path — during "dual" mode both
 * localStorage and cookies are written; during "cookie-only" only
 * cookies are used (with backend coordination for HttpOnly).
 */

const COOKIE_NAME = "auth_token";

/**
 * Parse all cookies into a key-value object.
 */
function getAllCookies() {
  const pairs = document.cookie.split(";").map((c) => c.trim());
  const map = {};
  for (const pair of pairs) {
    const idx = pair.indexOf("=");
    if (idx === -1) continue;
    const key = pair.slice(0, idx).trim();
    const val = decodeURIComponent(pair.slice(idx + 1).trim());
    map[key] = val;
  }
  return map;
}

/**
 * Write a cookie with Secure and SameSite attributes.
 * maxAgeSeconds defaults to 24 hours (86400).
 */
export function setAuthCookie(token, maxAgeSeconds = 86400) {
  if (!token) return;
  // Build cookie string with security flags
  const flags = [
    `max-age=${maxAgeSeconds}`,
    "path=/",
    "SameSite=Strict",
  ];
  // Only add Secure if on HTTPS (or localhost with Secure works in some contexts)
  if (window.location.protocol === "https:") {
    flags.push("Secure");
  }
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(token)}; ${flags.join("; ")}`;
}

/**
 * Read the auth token from cookies.
 * Returns the token string or null.
 */
export function getAuthCookie() {
  const cookies = getAllCookies();
  return cookies[COOKIE_NAME] || null;
}

/**
 * Remove the auth cookie by expiring it.
 */
export function clearAuthCookie() {
  document.cookie = `${COOKIE_NAME}=; max-age=0; path=/`;
}

/**
 * Decode JWT payload without verification.
 * Returns the parsed payload object or null.
 */
function decodeJwtPayload(token) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    return JSON.parse(atob(parts[1]));
  } catch {
    return null;
  }
}

/**
 * Check if the JWT token stored in the cookie is expired.
 */
export function isTokenExpiredCookie() {
  const token = getAuthCookie();
  if (!token) return true;
  const payload = decodeJwtPayload(token);
  if (!payload || !payload.exp) return true;
  return payload.exp < Math.floor(Date.now() / 1000);
}

/**
 * Get the full user object from the cookie (must include user data
 * in the JWT payload or reconstruct from the token payload).
 * Falls back to null.
 */
export function getUserFromCookie() {
  const token = getAuthCookie();
  if (!token) return null;
  const payload = decodeJwtPayload(token);
  if (!payload) return null;

  // Reconstruct user object from JWT payload
  // (assumes backend includes these claims)
  return {
    accessToken: token,
    id: payload.id,
    email: payload.email,
    firstName: payload.firstName || payload.firstname,
    lastName: payload.lastName || payload.lastname,
    roles: payload.roles || [],
    tcc: payload.tcc,
    availableBalance: payload.availableBalance,
    transactionRefundableFee: payload.transactionRefundableFee,
    pendingPayment: payload.pendingPayment,
    imageURL: payload.imageURL,
  };
}